/**
 * What one tile in "Connect a source" says and offers, from the same three
 * independent facts the Data Sources dialog reads: whether the system HAS DATA
 * (registry), whether it is CONNECTED (a firm connection, which locks Connect),
 * and whether it is CONNECTABLE at all (a built OAuth handler).
 */
import type { DataSource } from "../../config/dataSources";
import type { RegisteredSource } from "../../types/dataSource";
import { formatSyncedAgo, hasData } from "../../utils/sourceStatus";

export type TileTone = "ok" | "warn" | "muted" | "off";

export interface ConnectTileState {
	tone: TileTone;
	status: string;
	/** The button, if the tile offers one. */
	action: { label: string; primary: boolean } | null;
}

export interface ConnectTileInput {
	dataSource: DataSource;
	row: RegisteredSource | undefined;
	now: number;
	locked: boolean;
	needsReauthorization: boolean;
}

export function connectTileState({
	dataSource,
	row,
	now,
	locked,
	needsReauthorization,
}: ConnectTileInput): ConnectTileState {
	if (needsReauthorization) {
		return {
			tone: "warn",
			status: "Paused · sign in again",
			action: { label: "Sign in", primary: true },
		};
	}
	const withData = row !== undefined && hasData(row.status);
	if (withData) {
		return {
			tone: "ok",
			// A stale sync reads as its age ("synced 3d ago"), not as an alarm.
			status: `${row.status === "demo_snapshot" ? "Demo snapshot" : "Connected"} · ${formatSyncedAgo(row.last_synced_at_ms, now)}`,
			// A system with data but no firm connection (a demo-seeded tenant)
			// keeps a way to connect the real thing.
			action:
				dataSource.isAvailable && !locked
					? { label: "Reconnect", primary: false }
					: null,
		};
	}
	if (locked) {
		// A connection with no registered sync yet: only the weaker claim is
		// known, so say exactly that.
		return {
			tone: "muted",
			status: "Connected · awaiting first sync",
			action: null,
		};
	}
	if (dataSource.isAvailable) {
		return {
			tone: "muted",
			status: dataSource.description ?? "Not connected",
			action: { label: "Connect", primary: false },
		};
	}
	return { tone: "off", status: "Coming soon", action: null };
}
